import { AssignmentDoc, CalendarDoc, DateUtils, PropertyValues, ResourceDoc, ScheduleDoc, TaskDoc } from 'common';
import { FieldDelta, ScheduleService, TaskDeltas } from 'src/global/service/ScheduleService';
import { DepthFirstGraphSort } from 'src/schedule/utils/DepthFirstGraphSort';
import { ScheduleCalculate } from 'src/schedule/utils/ScheduleCalculate';
import { Calendar } from './Calendar';
import { FilterDoc, ScheduleFilter } from './ScheduleFilter';
import { DependencyType, Task, TreeNodeRelationship } from './Task';
import { ChangeEvent, ConcourseDb, DbListener } from 'src/global';
import { CalendarUtils } from '../utils/CalendarUtils';
import { TaskCreateData } from 'src/components';
import { ScheduleWorkService } from 'src/global/service/ScheduleWorkService';
import _ from 'lodash';

export class ScheduleData extends EventTarget {
  static readonly LOADING_MESSAGE: string = 'SCHEDULE_DATA_LOADING_MESSAGE';
  static readonly LOAD_COMPLETE: string = 'SCHEDULE_DATA_LOAD_COMPLETE';
  static readonly CHANGE: string = 'SCHEDULE_DATA_CHANGE';
  static readonly REBUILD: string = 'SCHEDULE_DATA_REBUILD';
  static readonly CPM_PREVIEW_ONLY: string = '__CPM_PREVIEW_ONLY__';

  private configDb: ConcourseDb;
  private calendarsDb: ConcourseDb;
  private tasksDb: ConcourseDb;
  private filtersDb: ConcourseDb;
  private resourcesDb: ConcourseDb;
  private assignmentsDb: ConcourseDb;

  private projectId: string;
  private _schedule: ScheduleDoc;
  private _tasks: TaskDoc[];
  private _resources: ResourceDoc[];
  private _assignments: AssignmentDoc[];

  public taskMap: Map<number, Task> = new Map();
  public treeRoot: Task = null;
  public minDate: Date = null;
  public maxDate: Date = null;
  public calendars: Calendar[];
  public defaultCalendar: Calendar = null;

  public filters: FilterDoc[] = [];
  public activeFilters: FilterDoc[] = [];

  constructor(projectId: string) {
    super();
    this.projectId = projectId;
  }

  public async loadScheduleData(): Promise<void> {
    this.configDb = ScheduleService.getScheduleConfigDb(this.projectId);
    this.configDb.addListeners(this.configDbListener);
    await this.configDb.syncRemote();

    this.calendarsDb = CalendarUtils.getCalendarsDb(this.projectId);
    this.calendarsDb.addListeners(this.calendarsDbListener);
    await this.calendarsDb.syncRemote();

    this.tasksDb = ScheduleService.getTasksDb(this.projectId);
    this.tasksDb.addListeners(this.tasksDbListener);
    await this.tasksDb.syncRemote();

    this.resourcesDb = ScheduleWorkService.getResourcesDb(this.projectId);
    this.resourcesDb.addListeners(this.resourcesDbListener);
    await this.resourcesDb.syncRemote();

    this.assignmentsDb = ScheduleWorkService.getAssignmentsDb(this.projectId);
    this.assignmentsDb.addListeners(this.assignmentsDbListener);
    await this.assignmentsDb.syncRemote();

    this.filtersDb = ScheduleFilter.getFilterDb(this.projectId);
    this.filtersDb.addListeners(this.filtersDbListener);
    await this.filtersDb.syncRemote();
  }

  private configDbListener: DbListener = {
    onComplete: (docs: ScheduleDoc[]) => {
      this._schedule = docs.find(s => s._id === 'settings');
      this.checkLoaded();
    },
    onChange: (event: ChangeEvent) => {
      this._schedule = event.docs.find(s => s._id === 'settings');
      this.checkLoaded();
    },
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  private calendarsDbListener: DbListener = {
    onComplete: (docs: CalendarDoc[]) => {
      this.calendars = CalendarUtils.parseCalendarDocs(docs);
      this.checkLoaded();
    },
    onChange: (event: ChangeEvent) => {
      this.calendars = CalendarUtils.parseCalendarDocs(event.docs);
      this.checkLoaded();
    },
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  private tasksDbListener: DbListener = {
    onProgress: (percent: number) => {
      this.dispatchEvent(new CustomEvent(ScheduleData.LOADING_MESSAGE, { detail: { message: `Loading tasks ${Math.round(percent * 100)}%` } }));
    },
    onComplete: (docs: TaskDoc[]) => {
      this._tasks = docs;
      this.checkLoaded();
    },
    // we get several events in succession, only handle the last one.
    onChange: _.debounce(
      (event: ChangeEvent) => {
        if (event.direction === 'pull') {
          if (!this.treeRoot && event.change.docs.length > 0) {
            this._tasks = event.docs;
            this.checkLoaded();
          } else {
            event.change.docs.forEach(doc => {
              console.log('ScheduleService.Task changed', doc.name);
              this.createOrUpdateTask(doc);
            });
            this.dispatchEvent(new Event(ScheduleData.CHANGE));
          }
        }
      },
      1000,
      { leading: true },
    ),
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  private resourcesDbListener: DbListener = {
    onComplete: (docs: ResourceDoc[]) => {
      this._resources = docs;
      this.checkLoaded();
    },
    onChange: (event: ChangeEvent) => {
      this._resources = event.docs;
      this.checkLoaded();
    },
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  private assignmentsDbListener: DbListener = {
    onComplete: (docs: AssignmentDoc[]) => {
      this._assignments = docs;
      this.checkLoaded();
    },
    onChange: (event: ChangeEvent) => {
      this._assignments = event.docs;
      this.checkLoaded();
    },
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  private filtersDbListener: DbListener = {
    onComplete: (docs: FilterDoc[]) => {
      this.filters = docs;
    },
    onChange: (event: ChangeEvent) => {
      this.filters = event.docs;
    },
    onError: (error: Error) => {
      console.error(error.message);
    },
  };

  get schedule(): ScheduleDoc {
    return this._schedule;
  }

  get duration(): number {
    return this.defaultCalendar.calculateDuration(this.startDate, this.finishDate);
  }

  get tasks(): TaskDoc[] {
    return this._tasks;
  }

  get resources(): ResourceDoc[] {
    return this._resources;
  }

  get assignments(): AssignmentDoc[] {
    return this._assignments;
  }

  get statusDate(): Date {
    return DateUtils.fromISO(this.schedule.property_values.status_date);
  }

  get startDate(): Date {
    return DateUtils.fromISO(this.schedule.property_values.start_date);
  }

  get finishDate(): Date {
    return DateUtils.fromISO(this.schedule.property_values.finish_date);
  }

  private checkLoaded(): void {
    const databasesLoaded = Array.isArray(this._tasks) && Array.isArray(this.calendars) && Array.isArray(this._resources) && Array.isArray(this._assignments);
    if (!databasesLoaded || this.treeRoot) return;
    // All data is loaded and its also populated.
    if (this._schedule && this._tasks.length > 0 && this.calendars.length > 0) {
      this.defaultCalendar = this.calendars.find(c => c.id === this._schedule.property_values.default_calendar_unique_id);

      if (this.tasksMissingDates().length > 0) {
        // this happens when we create a scheudle using AI and don't come with dates.
        const deltas = this.runClientCalculation();
        this.applyDeltas(deltas);
      }

      this.buildTree();
    }
    // needs to be outside of the if statement because those arrays are empty before any schedule is created.
    this.dispatchEvent(new CustomEvent(ScheduleData.LOAD_COMPLETE));
  }

  getTaskByUniqueId(unique_id: number): TaskDoc {
    return this.tasks.find(t => t.unique_id == unique_id);
  }

  private createTask(doc: TaskDoc): Task {
    console.log('createTreeNode', doc);
    const node = new Task(doc, this.getCalendar(doc));
    this.taskMap.set(doc.unique_id, node);
    this.tasks.push(doc);
    return node;
  }

  getCalendar(task: TaskDoc): Calendar {
    return task.calendar_unique_id ? this.calendars.find(c => c.id === task.calendar_unique_id) : this.defaultCalendar;
  }

  getCalendarFromUniqueId(unique_id: number): Calendar {
    return this.calendars.find(c => c.id === unique_id);
  }

  public async savePropertyValues(propertyValues: PropertyValues, recalculate: boolean = false): Promise<Map<number, FieldDelta[]>> {
    const db = ScheduleService.getScheduleConfigDb(this.projectId);
    const scheduleDoc = await db.saveDoc({ ...this.schedule, property_values: propertyValues });
    this._schedule = scheduleDoc;
    this.dispatchEvent(new Event(ScheduleData.CHANGE));
    if (recalculate) {
      await db.awaitSync();
      return await ScheduleService.runServerCalculation(this.projectId);
    }
    return null;
  }

  public async saveTasks(docs: TaskDoc[], awaitSync: boolean = false): Promise<TaskDoc[]> {
    const savedDocs = await ScheduleService.saveTasks(this.projectId, docs, awaitSync);
    savedDocs.forEach(task => {
      this.createOrUpdateTask(task);
    });
    this.dispatchEvent(new Event(ScheduleData.CHANGE));
    return savedDocs;
  }

  /**
   * This method runs CPM on the tasks and saves the changes and all the deltas to the database.
   * When we have MPXJ running, then the changes are saved by the Java lambda function.
   * This is important to avoid conflicts.
   * When we are in preview mode, then we calculate all the downstream affected tasks
   * and save orignally modified tasks and the downstream affected tasks.
   * @param docs
   */
  public async saveTasksAndRecalculate(docs: TaskDoc[]): Promise<void> {
    docs.forEach(doc => {
      this.createOrUpdateTask(doc);
    });
    const preview = this.runClientCalculation();
    const previewUpdatedTasks = this.applyDeltas(preview);

    // This is for local development only, where we are not calling MPXJ to do the actual calculation.
    // we update all docs that were affected by the preview calculation.
    if (ScheduleData.CPM_PREVIEW_ONLY == 'true') {
      await this.saveTasks(previewUpdatedTasks);
    } else {
      // dispatch the change event to update the UI with the preview updated values.
      this.dispatchEvent(new Event(ScheduleData.CHANGE));
      // first we save the tasks, and wait for the db to sync to couchdb before running the calculation.
      // We only update the original docs with the preview updated values.
      const docsWithPreview = docs.map(d => ({ ...d, ...previewUpdatedTasks.find(t => t.unique_id == d.unique_id) }));
      await this.saveTasks(docsWithPreview, true);
      //await this.runServerCalculation();
    }
  }

  private runClientCalculation(): TaskDeltas {
    const startDate = DateUtils.fromISO(this.schedule.property_values.start_date);
    const statusDate = DateUtils.fromISO(this.schedule.property_values.status_date);
    const calc = new ScheduleCalculate(this, startDate, statusDate);

    // we need the upstream task in the saved tasks list to be used as the root for the calculation.
    const rootTaskDoc = ScheduleService.getRootTaskDoc(this.tasks);

    return calc.previewCPM(rootTaskDoc, 9999);
  }

  private calculating: boolean = false;

  public async runServerCalculation(): Promise<void> {
    if (this.calculating) {
      return;
    }
    this.calculating = true;
    const deltas = await ScheduleService.runServerCalculation(this.projectId);
    console.log('deltas', deltas);
    if (deltas.size > 0) {
      this.applyDeltas(deltas).forEach(doc => {
        this.createOrUpdateTask(doc);
      });
    }
    this.dispatchEvent(new Event(ScheduleData.CHANGE));

    // must be false so that the next calculation can run.
    this.calculating = false;
  }

  public applyDeltas(deltas: Map<number, FieldDelta[]>): TaskDoc[] {
    const updatedTasks: TaskDoc[] = [];
    deltas.forEach((deltas: FieldDelta[], unique_id: number) => {
      const doc = this.tasks.find(t => t.unique_id == unique_id);
      if (!doc) {
        console.error('Task not found', unique_id);
        return;
      }
      deltas.forEach(delta => {
        doc[delta.field] = delta.new_value;
      });
      updatedTasks.push(doc);
    });
    return updatedTasks;
  }

  private createOrUpdateTask(doc: TaskDoc): Task {
    let task = this.taskMap.get(doc.unique_id);
    if (task) {
      task.doc = { ...task.doc, ...doc }; // some values might not be saved yet, such as color.
    } else {
      task = this.createTask(doc);
    }

    doc.predecessors &&
      doc.predecessors.forEach(predecessor => {
        const predecessorId = predecessor.predecessor_task_unique_id;
        const predecessorTask = this.taskMap.get(predecessorId);
        if (!predecessorTask) {
          console.error('Predecessor task not found', predecessorId);
          return;
        }
        if (predecessorTask.id == task.id) {
          console.error(`Trying to add a predecessor to task ${task.id} that is the same as the task`, predecessor);
          return;
        }
        if (task.predecessors.find(p => p.task == predecessorTask)) {
          const rel = task.predecessors.find(p => p.task == predecessorTask);
          rel.type = predecessor.type as DependencyType;
          rel.lag = predecessor.lag;
        } else {
          task.predecessors.push(new TreeNodeRelationship(predecessorTask, predecessor.type as DependencyType, predecessor.lag));
        }
        if (predecessorTask.successors.find(s => s.task == task)) {
          const rel = predecessorTask.successors.find(s => s.task == task);
          rel.type = predecessor.type as DependencyType;
          rel.lag = predecessor.lag;
        } else {
          predecessorTask.successors.push(new TreeNodeRelationship(task, predecessor.type as DependencyType, predecessor.lag));
        }
      });

    doc.successors &&
      doc.successors.forEach(successor => {
        const successorId = successor.successor_task_unique_id;
        const successorTask = this.taskMap.get(successorId);
        if (!successorTask) {
          console.error('Successor task not found', successorId);
          return;
        }
        if (successorTask.id == task.id) {
          console.error(`Trying to add a successor to task ${task.id} that is the same as the task`, successor);
          return;
        }
        if (task.successors.find(s => s.task == successorTask)) {
          const rel = task.successors.find(s => s.task == successorTask);
          rel.type = successor.type as DependencyType;
          rel.lag = successor.lag;
        } else {
          task.successors.push(new TreeNodeRelationship(successorTask, successor.type as DependencyType, successor.lag));
        }
        if (successorTask.predecessors.find(p => p.task == task)) {
          const rel = successorTask.predecessors.find(p => p.task == task);
          rel.type = successor.type as DependencyType;
          rel.lag = successor.lag;
        } else {
          successorTask.predecessors.push(new TreeNodeRelationship(task, successor.type as DependencyType, successor.lag));
        }
      });

    return task;
  }

  public showSearchCount(filteredTasks: TaskDoc[]): void {
    this.taskMap.forEach(task => {
      task.clearSearchCount();
    });
    filteredTasks.forEach(task => {
      this.taskMap.get(task.unique_id).incrementSearchCount();
      if (this.taskMap.get(task.unique_id).name == 'Structural') {
        console.log('Structural', this.taskMap.get(task.unique_id).searchCount);
      }
    });

    this.dispatchEvent(new Event(ScheduleData.CHANGE));
  }

  public clearSearchCount(): void {
    this.taskMap.forEach(task => {
      task.clearSearchCount();
    });
    this.dispatchEvent(new Event(ScheduleData.CHANGE));
  }

  public get filterIds(): string {
    return this.activeFilters.map(filter => filter._id).join(',');
  }

  public set filterIds(filterIds: string) {
    if (filterIds) {
      const filters = this.filters.filter(filter => filterIds.split(',').includes(filter._id));
      this.activeFilters = filters;
      this.filterTreeFilterDocs(this.activeFilters);
    } else if (this.activeFilters.length > 0) {
      this.activeFilters = [];
      this.filterTreeFilterDocs(this.activeFilters);
    }
  }

  public addFilter(filterId: string) {
    const filter = this.filters.find(filter => filter._id == filterId);
    if (filter) {
      this.activeFilters.push(filter);
      this.filterTreeFilterDocs(this.activeFilters);
    }
  }

  public removeFilter(filterId: string) {
    this.activeFilters = this.activeFilters.filter(filter => filter._id != filterId);
    this.filterTreeFilterDocs(this.activeFilters);
  }

  public filterTree(filterIds: string[]): void {
    const filters = this.filters.filter(filter => filterIds.includes(filter._id));
    this.filterTreeFilterDocs(filters);
  }

  private filterTreeFilterDocs(filters: FilterDoc[]): void {
    let filteredTasks: TaskDoc[] = this.tasks;
    if (filters && filters.length > 0) {
      for (const filter of filters) {
        filteredTasks = ScheduleFilter.filterTasks(filteredTasks, filter);
      }
      const uniqueIds = new Set(filteredTasks.map(task => task.unique_id));
      filteredTasks.forEach(task => {
        while (task.parent_task_unique_id && !uniqueIds.has(task.parent_task_unique_id)) {
          const parentTask = this.tasks.find(t => t.unique_id == task.parent_task_unique_id);
          if (parentTask) {
            filteredTasks.push(parentTask);
            uniqueIds.add(parentTask.unique_id);
          }
          task = parentTask;
        }
      });
    }
    this.buildTree();
    this.dispatchEvent(new Event(ScheduleData.REBUILD));
  }

  private tasksMissingDates(): TaskDoc[] {
    return this.tasks.filter(t => !t.actual_start || !t.actual_finish || !t.planned_start || !t.planned_finish);
  }

  public buildTree(): void {
    console.log('buildTree', this.tasks, this.calendars, this.defaultCalendar);
    const { treeRoot, map } = ScheduleService.parseIntoTree(this.schedule, this.tasks, this.calendars, this.defaultCalendar);
    this.treeRoot = treeRoot;
    this.taskMap = map;
    if (this.activeFilters && this.activeFilters.find(f => f.dateFilterType != 'none')) {
      const dateFilter = this.activeFilters.find(f => f.dateFilterType != 'none');
      this.minDate = DateUtils.fromISO(dateFilter.startDate);
      this.maxDate = DateUtils.fromISO(dateFilter.finishDate);
    } else {
      const { startDate, endDate } = ScheduleFilter.getStartEndFromTasks(this.treeRoot.children, this.schedule);
      this.minDate = startDate;
      this.maxDate = endDate;
    }
  }

  public async createNewTask(parent: Task, taskData: TaskCreateData, userId: string): Promise<Task> {
    console.log('createNewTask', parent, name);
    const calendar = this.getCalendar(parent.doc) || this.defaultCalendar;

    const taskDoc = ScheduleService.createTaskDoc(calendar, this.schedule?.property_values, parent, taskData, this.getNextUniqueId(), userId);

    // Save to database
    const savedTask = await ScheduleService.saveTaskDoc(this.projectId, taskDoc);
    const newTask = this.createTask(savedTask);
    newTask.level = parent.level + 1; // not a stored property, gets set after Task gets created.
    parent.addChild(newTask);
    parent.sortDecendents();
    this.dispatchEvent(new Event(ScheduleData.REBUILD));
    return newTask;
  }

  public getNextUniqueId(): number {
    return (
      this.tasks.reduce((max, task) => {
        return Math.max(max, task.unique_id || 0);
      }, 0) + 1
    );
  }

  public async deleteTask(task: Task) {
    this.taskMap.delete(task.doc.unique_id);
    this._tasks = this._tasks.filter(t => t.unique_id != task.doc.unique_id);
    task.parent.removeChild(task);
    this.dispatchEvent(new Event(ScheduleData.REBUILD)); // do this before the slow process of deleting remotely and recalculating.
    await ScheduleService.deleteTask(this.projectId, task.doc, true);
    await this.saveTasksAndRecalculate([task.parent.doc]);
  }

  public dispose(): void {
    this.configDb?.removeListeners(this.configDbListener);
    this.configDb = null;

    this.calendarsDb?.removeListeners(this.calendarsDbListener);
    this.calendarsDb = null;

    this.tasksDb?.removeListeners(this.tasksDbListener);
    this.tasksDb = null;

    this.resourcesDb?.removeListeners(this.resourcesDbListener);
    this.resourcesDb = null;

    this.assignmentsDb?.removeListeners(this.assignmentsDbListener);
    this.assignmentsDb = null;

    this.filtersDb?.removeListeners(this.filtersDbListener);
    this.filtersDb = null;

    this.taskMap.clear();
    this.treeRoot = null;
    this.minDate = null;
    this.maxDate = null;
    this.calendars = [];
    this.defaultCalendar = null;
    this.filters = [];
    this.activeFilters = [];
  }
}
